import _ from 'lodash';
import { DiagnosisResponse, ChartData, ChartSeries, DiagnosisTableData, InpatientDischargeDiagnosesResponse } from '../../types';
import { IPatient } from '../../../../../constants/types';
import { buildPatientSexMap, sexSplitFor } from '../../utils/diagnosisSexSplit';

/**
 * Transforms diagnosis statistics into appointment counts chart data
 * @param diagnosisData The raw diagnosis response data
 * @returns Formatted data for appointments chart
 */
export const transformAppointmentsData = (diagnosisData: DiagnosisResponse): ChartData => {
  if (!diagnosisData?.stats) {
    return {
      series: [],
      categories: []
    };
  }

  // Sort diagnoses by name
  const sortedDiagnoses = _.orderBy(
    Object.entries(diagnosisData.stats),
    ([_, stats]) => stats.name.toLowerCase(),
    ['asc']
  );

  return {
    series: [{
      name: 'Total Appointments',
      data: sortedDiagnoses.map(([_, stats]) => stats.amount)
    }],
    categories: sortedDiagnoses.map(([_, stats]) => stats.name)
  };
};

/**
 * SHER-583. Builds the rows the outpatient ICD-11 table renders.
 *
 * It used to return `ChartData` carrying three index-aligned arrays plus a three-series block
 * whose Female/Male members were filled with zeroes for the table to overwrite. Every consumer
 * read them back positionally, so the correctness of each row rested on an alignment no type
 * expressed — and SHER-880 is on record as the bug where that alignment silently broke and every
 * row reported the alphabetically first diagnosis's patients. One row object per diagnosis cannot
 * come apart from itself.
 *
 * The API-lag defaults live here, applied once as each row is built, rather than as a `|| ''` and
 * a `?? []` at every cell in the component.
 *
 * `patients` are the decrypted patients the hook already holds. The Female/Male split is counted
 * here rather than at render because patient sex is encrypted — the API can only send ids — and
 * this is where the rows are assembled. Resolving it in the chart layer instead meant every chart
 * card on every tab subscribing to the patient store to serve one table.
 */
export const transformUniquePatientData = (
  diagnosisData: DiagnosisResponse,
  patients?: IPatient[],
): DiagnosisTableData => {
  if (!diagnosisData?.stats) {
    return { rows: [] };
  }

  // Built once per response, not once per row and not once per render.
  const patientSexById = buildPatientSexMap(patients || []);

  // Sorted by name here rather than in the table: the API orders by its own ranking, which is
  // about which ten diagnoses earn a row, not about how they read once chosen.
  const sortedDiagnoses = _.orderBy(
    Object.entries(diagnosisData.stats),
    ([, stats]) => stats.name.toLowerCase(),
    ['asc'],
  );

  return {
    rows: sortedDiagnoses.map(([code, stats]) => ({
      // The entry key IS the ICD-11 code and always has been; `stats.code` is the same value
      // stated as a field. Preferring the field and falling back to the key keeps the column
      // filled against an API that predates it, since the frontend deploys separately.
      code: stats.code ?? code,
      name: stats.name,
      departments: stats.departments ?? [],
      patients: stats.patientsLength,
      ...sexSplitFor(stats.patientIds, patientSexById),
    })),
  };
};

/**
 * SHER-583. The inpatient discharge response, resolved into the rows the shared diagnosis table
 * renders — the counterpart of `transformUniquePatientData` above, and here beside it rather than
 * inlined in `useInpatientsData` so the two ICD-11 panels assemble their rows the same way and
 * this one can be tested without mounting a hook.
 */
export const transformDischargeDiagnoses = (
  response: InpatientDischargeDiagnosesResponse,
  patients?: IPatient[],
): DiagnosisTableData => {
  const patientSexById = buildPatientSexMap(patients || []);

  return {
    rows: (response.stats.diagnoses || []).map((diagnosis) => ({
      code: diagnosis.code,
      name: diagnosis.name,
      departments: diagnosis.departments,
      // `null`, not 0. The client deploys separately from the API and routinely ships first, so
      // this field really is absent in production — and "the API did not send this" is a
      // different statement from "no patients had this diagnosis". Collapsing them prints a
      // confident zero beside a real, non-zero Discharges count.
      patients: diagnosis.patientsLength ?? null,
      discharges: diagnosis.discharges,
      ...sexSplitFor(diagnosis.patientIds, patientSexById),
    })),
  };
};
